import { isOwnerSupportGhost } from "./isOwnerSupportGhost.js";

/**
 * Equals SUPER_ADMIN_ROLE and the server `SEAT_EXCLUDED_ROLE`. Kept local so this
 * module stays importable by `node --test` without the app constants graph.
 */
export const SEAT_EXCLUDED_ROLE = "superAdmin";

export const SEAT_LIMIT_REACHED_CODE = "seatLimitReached";

/** Quantity-addon key sent on every seats call. Agency comes from the token. */
export const SEATS_ADDON_KEY = "seats";

/** Generic quantity verbs. Seats thunks keep their names and call these. */
export const SEATS_QUANTITY_FUNCTION = Object.freeze({
  PREVIEW: "previewFeatureAddonProration",
  CHECKOUT: "createFeatureAddonCheckout",
  COMPLETE: "completeFeatureAddonCheckout",
});

export const SEATS_ERROR_CODE = Object.freeze({
  QUANTITY_CHANGED: "quantityChanged",
  OPERATION_IN_PROGRESS: "quantityOperationInProgress",
  UNPAID: "quantityUnpaid",
  OUT_OF_RANGE: "quantityOutOfRange",
  NOT_ELIGIBLE: "quantityNotEligible",
  PRICE_NOT_CONFIGURED: "quantityPriceNotConfigured",
  SUBSCRIPTION_NOT_FOUND: "quantitySubscriptionNotFound",
  ROLE_INVALID: "accountRoleInvalid",
  FORBIDDEN: "forbidden",
  MISSING_PARAMETERS: "missingParameters",
});

export const SEATS_ELIGIBILITY_REASON = Object.freeze({
  SELF_SERVE: "self_serve",
  APPSUMO: "appsumo",
  ENTERPRISE: "enterprise",
  SEATS_DISABLED: "seats_disabled",
  PLAN_INACTIVE: "plan_inactive",
  NO_USER_LIMIT: "no_user_limit",
});

export const CREATE_USER_ACTION = Object.freeze({
  FORM: "form",
  BUY_SEATS: "buySeats",
  UPGRADE_PLAN: "upgradePlan",
});

export const SEATS_CHANGE_OUTCOME = Object.freeze({
  APPLIED: "applied",
  SCHEDULED: "scheduled",
  CHECKOUT: "checkout",
  QUANTITY_CHANGED: "quantityChanged",
  IN_PROGRESS: "inProgress",
  UNPAID: "unpaid",
  FORBIDDEN: "forbidden",
  OUT_OF_RANGE: "outOfRange",
  NOT_ELIGIBLE: "notEligible",
  MISSING_PARAMETERS: "missingParameters",
  ERROR: "error",
});

export const SEATS_CHECKOUT_QUERY = "seatsCheckout";
export const SEATS_CHECKOUT_SUCCESS = "success";
export const SEATS_CHECKOUT_CANCEL = "cancel";
export const SEATS_RETURN_TO = Object.freeze({
  USERS: "users",
  BILLING: "billing",
});

export const EXTRA_SEATS_DRAFT_STORAGE_KEY = "extraSeatsDraftUser";

export const seatsPreviewRequest = ({ targetQuantity }) => ({
  functionName: SEATS_QUANTITY_FUNCTION.PREVIEW,
  data: { addonKey: SEATS_ADDON_KEY, targetQuantity },
});

export const seatsCheckoutRequest = ({
  targetQuantity,
  expectedQuantity,
  requestID,
  origin,
  returnTo,
}) => ({
  functionName: SEATS_QUANTITY_FUNCTION.CHECKOUT,
  data: {
    addonKey: SEATS_ADDON_KEY,
    targetQuantity,
    expectedQuantity,
    requestID,
    origin,
    returnTo,
  },
});

export const seatsCompleteCheckoutRequest = ({ sessionID }) => ({
  functionName: SEATS_QUANTITY_FUNCTION.COMPLETE,
  data: { addonKey: SEATS_ADDON_KEY, sessionID },
});

const DRAFT_TEXT_FIELDS = Object.freeze(["name", "email", "role"]);

const finiteNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Billable members for seat usage. Deleted users, super admins, and owner
 * login-as support ghosts do not occupy a seat. Ghost detection reuses
 * isOwnerSupportGhost, the same predicate as the owner table.
 */
export const countBillableUsers = (users) =>
  Object.values(users || {}).filter(
    (user) =>
      user &&
      user.isDeleted !== true &&
      user.role !== SEAT_EXCLUDED_ROLE &&
      !isOwnerSupportGhost(user)
  ).length;

const SEATS_ENTERPRISE_PLAN = "enterprise";
const SEATS_CATALOG_METER = "quantity";
const SEATS_CATALOG_GATE_MODES = Object.freeze(["off", "shadow", "enforce"]);

/** Same fallback the server uses when the seats catalog node is missing or invalid. */
const SEATS_CATALOG_FALLBACK = Object.freeze({
  meter: SEATS_CATALOG_METER,
  currency: "usd",
  interval: "month",
  maxQuantity: 50,
  gateMode: "off",
  enabled: false,
});

const seatsCatalogIsValid = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  if (entry.meter !== SEATS_CATALOG_METER) return false;
  if (entry.currency != null && entry.currency !== "usd") return false;
  if (entry.interval != null && entry.interval !== "month") return false;
  const maxQuantity = Number(entry.maxQuantity);
  if (!Number.isInteger(maxQuantity) || maxQuantity < 1) return false;
  if (!SEATS_CATALOG_GATE_MODES.includes(entry.gateMode)) return false;
  if (entry.enabled != null && typeof entry.enabled !== "boolean") return false;
  if (entry.unitPriceMonthly != null && entry.unitPriceMonthly !== "") {
    const price = Number(entry.unitPriceMonthly);
    if (!Number.isFinite(price) || price < 0) return false;
  }
  return true;
};

const resolveSeatsCatalog = (remote) => {
  if (!remote || typeof remote !== "object")
    return { ...SEATS_CATALOG_FALLBACK };
  const merged = { ...SEATS_CATALOG_FALLBACK, ...remote };
  if (!seatsCatalogIsValid(merged)) return { ...SEATS_CATALOG_FALLBACK };
  return merged;
};

const seatsPlanAllowance = (planInfo) => planInfo?.benefits?.users;

/**
 * Same eligibility order as the server quantity gate.
 * First match wins. The gate does not depend on canBuy.
 */
const evaluateSeatsEligibility = ({
  config = null,
  isAppSumo = false,
  planInfo = null,
  subscriptionPlan = null,
  isPlanActive = false,
} = {}) => {
  const rawAllowance = seatsPlanAllowance(planInfo);
  const allowance = Number(rawAllowance);
  if (rawAllowance == null || !Number.isFinite(allowance)) {
    return { canBuy: false, gateApplies: false, reason: "no_user_limit" };
  }
  if (
    planInfo?.isCustom === true ||
    subscriptionPlan === SEATS_ENTERPRISE_PLAN
  ) {
    return { canBuy: false, gateApplies: true, reason: "enterprise" };
  }
  const price = Number(config?.unitPriceMonthly);
  const priceReady = Number.isFinite(price) && price > 0;
  if (config?.enabled !== true || !priceReady) {
    return { canBuy: false, gateApplies: true, reason: "seats_disabled" };
  }
  if (isAppSumo === true) {
    return { canBuy: true, gateApplies: true, reason: "appsumo" };
  }
  if (isPlanActive !== true) {
    return { canBuy: false, gateApplies: true, reason: "plan_inactive" };
  }
  return { canBuy: true, gateApplies: true, reason: "self_serve" };
};

const resolveSeatsEntitlement = ({ state = null, now }) => {
  const quantityValue = Number(state?.quantity);
  const quantity = Number.isFinite(quantityValue) ? quantityValue : 0;
  const paidThrough = Number(state?.paidThrough);
  const paidQuantity = Number(state?.paidQuantity);
  const rawValidPaid =
    Number.isFinite(paidThrough) &&
    paidThrough > now &&
    Number.isFinite(paidQuantity)
      ? paidQuantity
      : 0;
  const billable =
    state?.isActive === true && state?.billingStatus !== "unpaid";
  const entitledSeats = billable ? Math.max(quantity, rawValidPaid) : 0;
  const pendingReduction =
    billable && rawValidPaid > quantity
      ? { from: rawValidPaid, to: quantity, effectiveAt: paidThrough }
      : null;
  return { entitledSeats, quantity, pendingReduction };
};

const resolveSeatsUnitPrice = ({ state, config }) => {
  const unitAmount = Number(state?.unitAmount);
  if (state?.stripeSubscriptionID && Number.isFinite(unitAmount)) {
    return unitAmount / 100;
  }
  const configured = Number(config?.unitPriceMonthly);
  if (Number.isFinite(configured) && configured > 0) return configured;
  return null;
};

const resolveSeatsCurrency = ({ state, config }) => {
  if (state?.currency) return String(state.currency).toUpperCase();
  if (config?.currency) return String(config.currency).toUpperCase();
  return "USD";
};

/**
 * Status previously returned by getFeatureAddonStatus for the seats addon.
 * Plan fields come from the selected advertiser (billing is copied to every brand).
 */
export const buildSeatsStatus = ({
  catalog = null,
  isAppSumo = false,
  planInfo = null,
  subscriptionPlan = null,
  isPlanActive = false,
  users = null,
  addonState = null,
  now = Date.now(),
} = {}) => {
  const config = resolveSeatsCatalog(catalog);
  const eligibility = evaluateSeatsEligibility({
    config,
    isAppSumo,
    planInfo,
    subscriptionPlan,
    isPlanActive,
  });
  const entitlement = resolveSeatsEntitlement({ state: addonState, now });
  const planUsers = seatsPlanAllowance(planInfo);
  const plan =
    planUsers == null || planUsers === "" || !Number.isFinite(Number(planUsers))
      ? null
      : Number(planUsers);
  const used = countBillableUsers(users);
  const entitled = Number(entitlement.entitledSeats) || 0;
  const quantity = Number(entitlement.quantity) || 0;
  const effectiveLimit = plan == null ? null : plan + entitled;
  const overage = Math.max(0, used - (plan ?? 0));
  const cap = Number(config.maxQuantity);
  return {
    canBuy: eligibility.canBuy,
    gateApplies: eligibility.gateApplies,
    reason: eligibility.reason,
    unitPriceMonthly: resolveSeatsUnitPrice({ state: addonState, config }),
    currency: resolveSeatsCurrency({ state: addonState, config }),
    planUsers: plan,
    usedSeats: used,
    quantity,
    entitledSeats: entitled,
    effectiveLimit,
    minQuantity: overage,
    maxQuantity: Number.isFinite(cap) && cap > 0 ? cap : 50,
    isActive: addonState?.isActive === true,
    billingStatus: addonState?.billingStatus || null,
    renewDate: addonState?.renewDate ?? null,
    cancelDateTime: addonState?.cancelDateTime ?? null,
    pendingReduction: entitlement.pendingReduction,
    hasSubscription: Boolean(addonState?.stripeSubscriptionID),
  };
};

export const resolveSeatUsage = ({
  seatsStatus = null,
  users = null,
  planUsersFallback = null,
} = {}) => {
  const localUsed = countBillableUsers(users);
  const hasServerLimit = Boolean(
    seatsStatus &&
      typeof seatsStatus === "object" &&
      ("effectiveLimit" in seatsStatus || "gateApplies" in seatsStatus)
  );

  if (!hasServerLimit) {
    return {
      source: "fallback",
      used: localUsed,
      limit: finiteNumberOrNull(planUsersFallback),
      canBuy: false,
      gateApplies: false,
    };
  }

  return {
    source: "server",
    used: finiteNumberOrNull(seatsStatus.usedSeats) ?? localUsed,
    limit: finiteNumberOrNull(seatsStatus.effectiveLimit),
    canBuy: seatsStatus.canBuy === true,
    gateApplies: seatsStatus.gateApplies === true,
  };
};

/** Form, buy-seats dialog, or the existing plan modal. */
export const resolveCreateUserAction = ({
  seatsStatus = null,
  users = null,
  planUsersFallback = null,
} = {}) => {
  const usage = resolveSeatUsage({ seatsStatus, users, planUsersFallback });
  if (usage.limit == null || usage.used < usage.limit) {
    return CREATE_USER_ACTION.FORM;
  }
  if (usage.canBuy) return CREATE_USER_ACTION.BUY_SEATS;
  return CREATE_USER_ACTION.UPGRADE_PLAN;
};

export const isSeatLimitReachedResult = (result) =>
  Boolean(
    result && result.ok === false && result.code === SEAT_LIMIT_REACHED_CODE
  );

/** 402 from manageAdsUser: buy a seat, or the plan modal when buying is closed. */
export const resolveSeatLimitReachedAction = (result) => {
  if (!isSeatLimitReachedResult(result)) return null;
  if (result.data?.canBuy === true) return CREATE_USER_ACTION.BUY_SEATS;
  return CREATE_USER_ACTION.UPGRADE_PLAN;
};

export const interpretChangeSeatsResult = (result) => {
  const data = result?.data ?? null;
  const status = data?.status;
  if (result?.ok === true && status === "applied") {
    return { outcome: SEATS_CHANGE_OUTCOME.APPLIED, data };
  }
  if (result?.ok === true && status === "scheduled") {
    return { outcome: SEATS_CHANGE_OUTCOME.SCHEDULED, data };
  }
  if (result?.ok === true && status === "checkout" && data?.paymentUrl) {
    return { outcome: SEATS_CHANGE_OUTCOME.CHECKOUT, data };
  }

  const code = result?.code;
  if (code === SEATS_ERROR_CODE.QUANTITY_CHANGED) {
    return { outcome: SEATS_CHANGE_OUTCOME.QUANTITY_CHANGED, data };
  }
  if (code === SEATS_ERROR_CODE.OPERATION_IN_PROGRESS) {
    return { outcome: SEATS_CHANGE_OUTCOME.IN_PROGRESS, data };
  }
  if (code === SEATS_ERROR_CODE.UNPAID) {
    return { outcome: SEATS_CHANGE_OUTCOME.UNPAID, data };
  }
  if (
    code === SEATS_ERROR_CODE.ROLE_INVALID ||
    code === SEATS_ERROR_CODE.FORBIDDEN
  ) {
    return { outcome: SEATS_CHANGE_OUTCOME.FORBIDDEN, data };
  }
  if (code === SEATS_ERROR_CODE.OUT_OF_RANGE) {
    return { outcome: SEATS_CHANGE_OUTCOME.OUT_OF_RANGE, data };
  }
  if (
    code === SEATS_ERROR_CODE.NOT_ELIGIBLE ||
    code === SEATS_ERROR_CODE.PRICE_NOT_CONFIGURED ||
    code === SEATS_ERROR_CODE.SUBSCRIPTION_NOT_FOUND
  ) {
    return { outcome: SEATS_CHANGE_OUTCOME.NOT_ELIGIBLE, data, code };
  }
  if (code === SEATS_ERROR_CODE.MISSING_PARAMETERS) {
    return { outcome: SEATS_CHANGE_OUTCOME.MISSING_PARAMETERS, data, code };
  }
  return { outcome: SEATS_CHANGE_OUTCOME.ERROR, data, code };
};

const REQUEST_ID_RESET_OUTCOMES = new Set([
  SEATS_CHANGE_OUTCOME.APPLIED,
  SEATS_CHANGE_OUTCOME.SCHEDULED,
  SEATS_CHANGE_OUTCOME.CHECKOUT,
  SEATS_CHANGE_OUTCOME.QUANTITY_CHANGED,
]);

/** Keep the same requestID when Stripe failed before opening Checkout. */
export const shouldReuseSeatsRequestID = (outcome) =>
  !REQUEST_ID_RESET_OUTCOMES.has(outcome);

export const SEATS_CHECKOUT_CONFIRMATION = Object.freeze({
  PAID: "paid",
  FORBIDDEN: "forbidden",
  INCOMPLETE: "incomplete",
});

/** `paid` only when the server confirmed the charge. A non-seats session is 403. */
export const interpretSeatsCheckoutConfirmation = (result) => {
  if (result?.ok === true && result?.data?.sessionStatus === "paid") {
    return SEATS_CHECKOUT_CONFIRMATION.PAID;
  }
  const code = result?.code;
  if (
    code === SEATS_ERROR_CODE.FORBIDDEN ||
    code === SEATS_ERROR_CODE.ROLE_INVALID
  ) {
    return SEATS_CHECKOUT_CONFIRMATION.FORBIDDEN;
  }
  return SEATS_CHECKOUT_CONFIRMATION.INCOMPLETE;
};

export const isReactivateBlockedReason = (reason) =>
  reason === SEATS_ELIGIBILITY_REASON.PLAN_INACTIVE ||
  reason === SEATS_ELIGIBILITY_REASON.APPSUMO;

export const SEATS_NOT_ELIGIBLE_MESSAGE = Object.freeze({
  REACTIVATE: "reactivate",
  PRICE: "price",
  SUBSCRIPTION: "subscription",
  GENERIC: "generic",
});

export const resolveSeatsNotEligibleMessage = ({
  code = "",
  reason = "",
  intent = "",
} = {}) => {
  if (
    code === SEATS_ERROR_CODE.NOT_ELIGIBLE &&
    (intent === "reactivate" || isReactivateBlockedReason(reason))
  ) {
    return SEATS_NOT_ELIGIBLE_MESSAGE.REACTIVATE;
  }
  if (code === SEATS_ERROR_CODE.PRICE_NOT_CONFIGURED) {
    return SEATS_NOT_ELIGIBLE_MESSAGE.PRICE;
  }
  if (code === SEATS_ERROR_CODE.SUBSCRIPTION_NOT_FOUND) {
    return SEATS_NOT_ELIGIBLE_MESSAGE.SUBSCRIPTION;
  }
  return SEATS_NOT_ELIGIBLE_MESSAGE.GENERIC;
};

/** Plan benefit `users` is a finite cap. Enterprise and unlimited plans omit it. */
export const planBenefitsIncludeUsers = (users) => {
  const value = Number(users);
  return Number.isFinite(value) && value > 0;
};

/**
 * The billing row renders from the plan already in state. A missing RTDB node
 * means quantity 0: the agency has not bought seats yet, and checkout creates it.
 */
export const shouldShowSeatsBillingRow = ({ planUsers = null } = {}) =>
  planBenefitsIncludeUsers(planUsers);

export const seatsBillingQuantity = (addonNode) => {
  const quantity = Number(addonNode?.quantity);
  if (!Number.isFinite(quantity) || quantity < 0) return 0;
  return quantity;
};

/** Renewal or cancellation date, only while the agency is paying for seats. */
export const seatsBillingSchedule = ({
  addonNode = null,
  now = Date.now(),
} = {}) => {
  const quantity = seatsBillingQuantity(addonNode);
  const subscriptionID = addonNode?.stripeSubscriptionID;
  const hasSubscription =
    typeof subscriptionID === "string" && subscriptionID.trim().length > 0;
  if (!hasSubscription && quantity <= 0) return null;

  const cancelAt = Number(addonNode?.cancelDateTime);
  const renewAt = Number(addonNode?.renewDate);
  const cancelScheduled =
    Number.isFinite(cancelAt) && cancelAt > 0 && now < cancelAt;
  const scheduleAt = cancelScheduled ? cancelAt : renewAt;
  if (!Number.isFinite(scheduleAt) || scheduleAt <= 0) return null;
  return {
    cancelScheduled,
    date: new Date(scheduleAt),
  };
};

/**
 * Target for "add one seat". Unpaid seats have entitledSeats 0, so basing it on
 * entitlement alone would ask the server for a reduction below `quantity`.
 */
export const resolveAddSeatTarget = (seatsStatus) =>
  Math.max(
    Number(seatsStatus?.entitledSeats) || 0,
    Number(seatsStatus?.quantity) || 0
  ) + 1;

export const canIncreaseSeats = (seatsStatus) =>
  Boolean(
    seatsStatus &&
      seatsStatus.canBuy === true &&
      seatsStatus.billingStatus !== "unpaid"
  );

export const SEATS_QUANTITY_FALLBACK_MAX = 50;

export const SEATS_QUOTE_ROW = Object.freeze({
  DELTA: "delta",
  DUE_TODAY: "dueToday",
  PRORATION: "proration",
  NEXT_FROM: "nextFrom",
  NEXT_MONTHLY: "nextMonthly",
  MONTHLY_TOTAL: "monthlyTotal",
});

export const SEATS_QUOTE_NOTE = Object.freeze({
  KEEP: "keep",
  PRORATE: "prorate",
  FREE: "free",
});

const nonNegativeQuantity = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return numeric;
};

export const seatsQuantityMin = (seatsStatus) => {
  const minQuantity = Number(seatsStatus?.minQuantity);
  if (Number.isFinite(minQuantity) && minQuantity > 0) return minQuantity;
  return 0;
};

export const seatsQuantityMax = (seatsStatus) => {
  const maxQuantity = Number(seatsStatus?.maxQuantity);
  if (Number.isFinite(maxQuantity) && maxQuantity > 0) return maxQuantity;
  return SEATS_QUANTITY_FALLBACK_MAX;
};

/** Active members sit above `minQuantity`. Going under it cannot be confirmed. */
export const seatsReductionBlock = ({
  seatsStatus = null,
  targetQuantity = 0,
} = {}) => {
  const min = seatsQuantityMin(seatsStatus);
  const target = nonNegativeQuantity(targetQuantity);
  if (target >= min) return null;
  return {
    usedSeats: finiteNumberOrNull(seatsStatus?.usedSeats),
    removeCount: min - target,
  };
};

/** Unknown `canBuy` stays open. An explicit refusal or an unpaid invoice locks +. */
export const isSeatsIncreaseLocked = (seatsStatus) =>
  Boolean(
    seatsStatus &&
      (seatsStatus.billingStatus === "unpaid" || seatsStatus.canBuy === false)
  );

/**
 * Billing opens on the current quantity. The user-limit paywall proposes one
 * more seat when the server says the agency can buy, including quantity 0.
 */
export const resolveSeatsModalQuantity = ({
  seatsStatus = null,
  purpose = "manage",
} = {}) => {
  const current = seatsBillingQuantity(seatsStatus);
  if (purpose !== "add" || !canIncreaseSeats(seatsStatus)) return current;
  return Math.min(
    seatsQuantityMax(seatsStatus),
    resolveAddSeatTarget(seatsStatus)
  );
};

export const canConfirmSeatsChange = ({
  currentQuantity = 0,
  targetQuantity = 0,
  blocked = false,
  unpaid = false,
  previewReady = false,
} = {}) => {
  if (blocked || unpaid || !previewReady) return false;
  return (
    nonNegativeQuantity(targetQuantity) !== nonNegativeQuantity(currentQuantity)
  );
};

/**
 * Price breakdown for the seats modal. Decrease totals can render before the
 * preview returns (`dueToday` 0). An increase waits on `dueToday` from
 * `previewFeatureAddonProration`.
 */
export const buildSeatsChangeQuote = ({
  currentQuantity = 0,
  targetQuantity = 0,
  unitPriceMonthly = 0,
  currency = "USD",
  seatsStatus = null,
  preview = null,
  now = Date.now(),
} = {}) => {
  const current = nonNegativeQuantity(currentQuantity);
  const target = nonNegativeQuantity(targetQuantity);
  const delta = target - current;
  const unit = Number(unitPriceMonthly);
  const unitPrice = Number.isFinite(unit) ? unit : 0;
  const block = seatsReductionBlock({ seatsStatus, targetQuantity: target });
  const previewData = preview && typeof preview === "object" ? preview : null;
  const resolvedCurrency =
    previewData?.currency || seatsStatus?.currency || currency || "USD";
  const renewDate = finiteNumberOrNull(
    previewData?.renewDate ?? seatsStatus?.renewDate
  );
  const dueFromPreview =
    previewData && previewData.dueToday != null
      ? finiteNumberOrNull(previewData.dueToday)
      : null;
  const dueToday =
    dueFromPreview != null ? dueFromPreview : delta < 0 ? 0 : null;
  const nextFromPreview =
    previewData && previewData.nextRenewalAmount != null
      ? finiteNumberOrNull(previewData.nextRenewalAmount)
      : null;
  const nextTotal =
    nextFromPreview != null ? nextFromPreview : target * unitPrice;

  const rows = [];
  if (delta === 0) {
    rows.push({
      type: SEATS_QUOTE_ROW.MONTHLY_TOTAL,
      amount: current * unitPrice,
    });
  } else {
    rows.push({
      type: SEATS_QUOTE_ROW.DELTA,
      delta,
      unitPrice,
      monthlyDelta: delta * unitPrice,
    });
    if (delta > 0) {
      const periodEnd = renewDate;
      const periodStart =
        finiteNumberOrNull(previewData?.periodStart) ??
        (periodEnd ? now : null);
      if (periodStart && periodEnd) {
        rows.push({
          type: SEATS_QUOTE_ROW.PRORATION,
          amount: dueToday,
          periodStart,
          periodEnd,
        });
      } else {
        rows.push({ type: SEATS_QUOTE_ROW.DUE_TODAY, amount: dueToday });
      }
      rows.push({ type: SEATS_QUOTE_ROW.NEXT_MONTHLY, amount: nextTotal });
    } else {
      rows.push({ type: SEATS_QUOTE_ROW.DUE_TODAY, amount: dueToday ?? 0 });
      if (renewDate) {
        rows.push({
          type: SEATS_QUOTE_ROW.NEXT_FROM,
          amount: nextTotal,
          date: renewDate,
        });
      } else {
        rows.push({ type: SEATS_QUOTE_ROW.NEXT_MONTHLY, amount: nextTotal });
      }
    }
  }

  let note = null;
  if (delta < 0) note = SEATS_QUOTE_NOTE.KEEP;
  else if (delta > 0 && dueToday === 0) note = SEATS_QUOTE_NOTE.FREE;
  else if (delta > 0 && dueToday != null && dueToday > 0) {
    note = SEATS_QUOTE_NOTE.PRORATE;
  }

  return {
    delta,
    current,
    target,
    currency: resolvedCurrency,
    rows,
    note,
    block,
  };
};

export const shouldOpenUserFormAfterCheckout = ({
  sessionStatus = "",
  seatsStatus = null,
} = {}) => {
  if (sessionStatus !== "paid" || !seatsStatus) return false;
  const limit = finiteNumberOrNull(seatsStatus.effectiveLimit);
  if (limit == null) return true;
  const used = finiteNumberOrNull(seatsStatus.usedSeats);
  if (used == null) return true;
  return used < limit;
};

export const sanitizeSeatDraftUser = (draft) => {
  if (!draft || typeof draft !== "object") return null;
  const next = {};
  for (const field of DRAFT_TEXT_FIELDS) {
    if (typeof draft[field] === "string" && draft[field].trim()) {
      next[field] = draft[field];
    }
  }
  if (Array.isArray(draft.advertisersAvailable)) {
    next.advertisersAvailable = draft.advertisersAvailable.filter(
      (id) => typeof id === "string" && id
    );
  }
  return Object.keys(next).length > 0 ? next : null;
};

const resolveDraftStorage = (storage) => {
  if (storage) return storage;
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage;
};

export const saveSeatDraftUser = ({ draft, storage } = {}) => {
  const safe = sanitizeSeatDraftUser(draft);
  const target = resolveDraftStorage(storage);
  if (!safe || !target) return null;
  try {
    target.setItem(EXTRA_SEATS_DRAFT_STORAGE_KEY, JSON.stringify(safe));
    return safe;
  } catch {
    return null;
  }
};

export const readSeatDraftUser = ({ storage } = {}) => {
  const target = resolveDraftStorage(storage);
  if (!target) return null;
  try {
    const raw = target.getItem(EXTRA_SEATS_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return sanitizeSeatDraftUser(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const clearSeatDraftUser = ({ storage } = {}) => {
  const target = resolveDraftStorage(storage);
  if (!target) return;
  try {
    target.removeItem(EXTRA_SEATS_DRAFT_STORAGE_KEY);
  } catch {
    /* sessionStorage can throw in private mode */
  }
};

export const stripSeatsCheckoutSearch = (search = "") => {
  const raw = String(search).startsWith("?")
    ? String(search).slice(1)
    : String(search);
  const next = new URLSearchParams(raw);
  if (!next.has(SEATS_CHECKOUT_QUERY)) return next;
  next.delete(SEATS_CHECKOUT_QUERY);
  next.delete("session_id");
  return next;
};

const seatCurrencyCode = (currency) =>
  typeof currency === "string" && currency.trim()
    ? currency.trim().toUpperCase()
    : "USD";

/** Whole amounts omit cents. A leading + is only added when `signed` is set. */
export const formatSeatAmount = ({
  amount,
  currency = "USD",
  locale = "en",
  signed = false,
} = {}) => {
  if (amount == null || amount === "") return "";
  const value = Number(amount);
  const code = seatCurrencyCode(currency);
  if (!Number.isFinite(value)) return "";
  const cents = Math.round(Math.abs(value) * 100);
  const fractionDigits = cents % 100 === 0 ? 0 : 2;
  let body = "";
  try {
    body = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(Math.abs(value));
  } catch {
    body = `${Math.abs(value)} ${code}`;
  }
  if (signed && value > 0) return `+${body}`;
  if (value < 0) return `-${body}`;
  return body;
};

export const seatsStatusFromCheckout = (data) => {
  if (!data || typeof data !== "object") return null;
  const status = { ...data };
  delete status.sessionStatus;
  return status;
};
